import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Stack, TextField, Alert,
  CircularProgress, Chip, FormControlLabel, Switch, Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import {
  getSystemEmailTemplate, updateSystemEmailTemplate, previewSystemEmailTemplate,
} from '../services/api';

const PLACEHOLDERS = [
  '{{firstName}}', '{{lastName}}', '{{email}}', '{{phone}}',
  '{{company}}', '{{jobTitle}}', '{{notes}}',
  '{{startTime}}', '{{endTime}}', '{{startTimeLocal}}', '{{durationMinutes}}',
  '{{hostName}}', '{{manageUrl}}', '{{bookUrl}}',
  '{{prospectTimezone}}', '{{cancelReason}}',
  '{{typeLabel}}', '{{typeKey}}', '{{joinUrl}}',
];

export default function SystemEmailTemplatePage() {
  const { key } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [plainTextBody, setPlainTextBody] = useState('');
  const [isActive, setIsActive] = useState(true);

  const [previewEmail, setPreviewEmail] = useState('');
  const [activeField, setActiveField] = useState('htmlBody');
  const subjectRef = useRef(null);
  const htmlRef = useRef(null);
  const plainRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await getSystemEmailTemplate(key);
      setName(data.name || '');
      setDescription(data.description || '');
      setSubject(data.subject || '');
      setHtmlBody(data.htmlBody || '');
      setPlainTextBody(data.plainTextBody || '');
      setIsActive(data.isActive !== false);
    } catch {
      setError('Failed to load template');
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => { load(); }, [load]);

  const insertPlaceholder = (placeholder) => {
    const refMap = { subject: subjectRef, htmlBody: htmlRef, plainTextBody: plainRef };
    const setterMap = { subject: setSubject, htmlBody: setHtmlBody, plainTextBody: setPlainTextBody };
    const valueMap = { subject, htmlBody, plainTextBody };

    const el = refMap[activeField]?.current;
    const setter = setterMap[activeField];
    const value = valueMap[activeField];
    if (!el || !setter) return;

    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = value.slice(0, start) + placeholder + value.slice(end);
    setter(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + placeholder.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const handleSave = async () => {
    setError(''); setSuccess('');
    setSaving(true);
    try {
      await updateSystemEmailTemplate(key, {
        subject, htmlBody, plainTextBody: plainTextBody || null, isActive,
      });
      setSuccess('Template saved.');
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    setError(''); setSuccess('');
    setPreviewing(true);
    try {
      const { data } = await previewSystemEmailTemplate(key, previewEmail || undefined);
      setSuccess(`Preview sent to ${data.to}`);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to send preview');
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/management?tab=email-templates')}>Back</Button>
      </Stack>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2, alignItems: { sm: 'flex-start' } }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5">{name}</Typography>
            <Typography variant="caption" color="text.secondary">key: {key}</Typography>
            {description && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{description}</Typography>}
          </Box>
          <FormControlLabel
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
            label={isActive ? 'Active' : 'Inactive'}
          />
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <TextField
          inputRef={subjectRef}
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          onFocus={() => setActiveField('subject')}
          fullWidth
          sx={{ mb: 2 }}
        />

        <TextField
          inputRef={htmlRef}
          label="HTML body"
          value={htmlBody}
          onChange={(e) => setHtmlBody(e.target.value)}
          onFocus={() => setActiveField('htmlBody')}
          multiline
          minRows={10}
          fullWidth
          sx={{ mb: 2, fontFamily: 'monospace' }}
          inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
        />

        <TextField
          inputRef={plainRef}
          label="Plain text body (optional)"
          value={plainTextBody}
          onChange={(e) => setPlainTextBody(e.target.value)}
          onFocus={() => setActiveField('plainTextBody')}
          multiline
          minRows={4}
          fullWidth
          sx={{ mb: 2 }}
          inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
        />

        <Box sx={{ mb: 2, width: '100%', minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            Click a placeholder to insert at the cursor (active field: <strong>{activeField}</strong>):
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.75,
              mt: 1,
              width: '100%',
              minWidth: 0,
            }}
          >
            {PLACEHOLDERS.map((p) => (
              <Chip
                key={p}
                label={p}
                size="small"
                onClick={() => insertPlaceholder(p)}
                sx={{ cursor: 'pointer', fontFamily: 'monospace', maxWidth: '100%' }}
              />
            ))}
          </Box>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Send preview</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Renders the template with sample data and emails it. Leave blank to send to your account email.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <TextField
            label="Send to"
            placeholder="leave blank for your own email"
            size="small"
            value={previewEmail}
            onChange={(e) => setPreviewEmail(e.target.value)}
            sx={{ minWidth: 280 }}
          />
          <Button variant="outlined" startIcon={<SendIcon />} onClick={handlePreview} disabled={previewing}>
            {previewing ? 'Sending…' : 'Send preview'}
          </Button>
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
          Live preview (sample data, no real send):
        </Typography>
        <Box sx={{
          border: '1px solid', borderColor: 'divider', borderRadius: 1,
          p: 2, background: 'background.default', maxHeight: 400, overflow: 'auto',
        }}>
          <div dangerouslySetInnerHTML={{ __html: renderLocally(htmlBody) }} />
        </Box>
      </Paper>
    </Box>
  );
}

// Quick client-side render for live preview using same sample placeholders
function renderLocally(html) {
  const sample = {
    firstName: 'Alex', lastName: 'Sample', email: 'alex.sample@example.com',
    phone: '+1 555 0100', company: 'Sample Industries', jobTitle: 'Head of Subscriptions',
    notes: 'Wants to see usage-based billing in action.',
    startTime: '2026-04-30 14:00', endTime: '2026-04-30 14:30',
    startTimeLocal: '2026-04-30 14:00 UTC', durationMinutes: '30',
    hostName: 'Azadeh',
    manageUrl: 'https://azotte.com/manage-booking.html?token=PREVIEW',
    bookUrl: 'https://azotte.com/book-demo.html',
    prospectTimezone: 'Europe/Berlin', cancelReason: '—',
    typeLabel: 'Demo', typeKey: 'demo',
    joinUrl: 'https://meet.google.com/xxx-yyyy-zzz',
  };
  return (html || '').replace(/\{\{(\w+)\}\}/g, (_, k) => sample[k] ?? `{{${k}}}`);
}
